import { Directive, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { QueueImageLoaderService } from 'src/app/services/queue-image-loader.service';

@Directive({
  selector: '[castoLazyImage]',
})
export class CastoLazyImageDirective implements OnInit, OnDestroy, OnChanges {
  @Input() castoLazyImage!: string;
  @Output() imageLoaded = new EventEmitter<boolean>(); // true = succès, false = erreur

  private readonly el: HTMLImageElement;
  private hasStarted = false;
  private destroyed = false;
  private reqId = 0;
  private lastRequestedSrc: string | null = null;

  private readonly imageLoaderService = inject(QueueImageLoaderService);

  private static io: IntersectionObserver | null = null;
  private static readonly registry = new WeakMap<Element, CastoLazyImageDirective>();

  constructor(ref: ElementRef<HTMLImageElement>) {
    this.el = ref.nativeElement;
    this.el.loading = 'lazy';
    this.el.decoding = 'async';
  }

  ngOnInit(): void {
    if (!('IntersectionObserver' in globalThis)) {
      this.startLoad();
      return;
    }

    if (!CastoLazyImageDirective.io) {
      CastoLazyImageDirective.io = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const dir = CastoLazyImageDirective.registry.get(entry.target);
            if (!dir) continue;
            CastoLazyImageDirective.io.unobserve(entry.target);
            dir.startLoad();
          }
        },
        { root: null, rootMargin: '500px 0px', threshold: 0.01 }
        //{ rootMargin: '10px' } // Commence à charger 50px avant
      );
    }

    CastoLazyImageDirective.registry.set(this.el, this);
    CastoLazyImageDirective.io.observe(this.el);
  }

  ngOnChanges(ch: SimpleChanges): void {
    // si la source change après initialisation, relancer proprement
    if (ch['castoLazyImage'] && !ch['castoLazyImage'].firstChange) {
      // invalide toute requête précédente
      this.reqId++;
      this.lastRequestedSrc = this.castoLazyImage || null;
      // évite d'aficher l'ancienne image pendant le rechargement
      try {
        this.el.src = '';
      } catch {}

      this.hasStarted = false;
      // si l'élément est déjà visible, relancer directement
      if (!CastoLazyImageDirective.io) {
        this.startLoad();
      } else {
        CastoLazyImageDirective.io.observe(this.el);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    CastoLazyImageDirective.io?.unobserve?.(this.el);
    CastoLazyImageDirective.registry.delete(this.el);
    // invalide les promesses en cours et libère
    this.reqId++;
    try {
      this.el.src = '';
    } catch {}
  }

  private async startLoad() {
    if (this.hasStarted || this.destroyed) return;
    this.hasStarted = true;
    const myReq = ++this.reqId;
    const expected = this.castoLazyImage;
    this.lastRequestedSrc = expected;

    try {
      // charger l'image via le service avant de l'affecter à l'élément
      const src = await this.imageLoaderService.loadImage(this.castoLazyImage);
      if (this.destroyed) return;

      // si l'input a changé entre-temps, ignorer cette requête
      if (myReq !== this.reqId || expected !== this.castoLazyImage) return;

      // attacher les handlers avant d'affecter la source
      const onLoad = () => {
        this.el.removeEventListener('load', onLoad);
        this.el.removeEventListener('error', onError);
        if (!this.destroyed) this.imageLoaded.emit(true);
      };

      const onError = () => {
        this.el.removeEventListener('load', onLoad);
        this.el.removeEventListener('error', onError);
        if (!this.destroyed) this.imageLoaded.emit(false);
      };

      this.el.addEventListener('load', onLoad, { once: true });
      this.el.addEventListener('error', onError, { once: true });

      // affecter la source pour démarrer le chargement
      this.el.src = src;
    } catch {
      // si l'input a changé entre-temps, ne pas émettre d'erreur
      if (myReq !== this.reqId || expected !== this.castoLazyImage) return;
      if (!this.destroyed) this.imageLoaded.emit(false);
    }
  }
}
